import React, { useEffect, useState } from "react";

import { AlignVAT } from "@vp/ep-pricing";
import { upperFunnelPages } from "../../constants/pageId";
import { useProductStore } from "../../store/productPriceDetails.store";
import { PageId } from "../../types/pageId";
import { DisplayVariant, PriceDisplayType, SelectionsWithQuantity } from "../../types/pricing/price-display.types";
import { sanitizeSelections } from "../../util/sanitizeSelections";
import { PriceDisplayV2, PriceDisplayV2Props } from "../PriceDisplayV2/PriceDisplayV2";
import { PriceDisplayProps } from "./PriceDisplay";

// LogoProcessingCharge is client-side only — never a valid API display strategy.
const VALID_PRICE_DISPLAY_TYPES = new Set<string>(
  Object.values(PriceDisplayType).filter((v) => v !== PriceDisplayType.LogoProcessingCharge),
);

const toValidPriceDisplayType = (value: string | undefined): PriceDisplayType | undefined => {
  if (value && VALID_PRICE_DISPLAY_TYPES.has(value)) {
    return value as PriceDisplayType;
  }
  return undefined;
};

const PriceDisplayTransitionWrapper = (props: PriceDisplayProps) => {
  const basePriceExtractionConfig = useProductStore((state) => state.basePriceExtractionConfig);
  const [pageId, setPageId] = useState<PageId | undefined>(basePriceExtractionConfig?.pageId);

  useEffect(() => {
    if (pageId) return;
    if (basePriceExtractionConfig?.pageId) {
      setPageId(basePriceExtractionConfig.pageId);
    }
  }, [basePriceExtractionConfig?.pageId, pageId]);

  //todo: remove this once all pages migrate to new pricing component
  const effectivePageId = pageId ?? basePriceExtractionConfig?.pageId;

  const priceDisplayProps: PriceDisplayV2Props = {
    mpvId: props.mpvId,
    productId: props.productId ?? props.productKey,
    version: props.version,
    selections: sanitizeSelections(props.selections, props.quantity) as SelectionsWithQuantity | undefined,
    alternateSelections: sanitizeSelections(props.alternateSelections),
    hideMinimumOrderableQuantity: props.hideMinimumOrderableQuantity,
    requestedPriceDisplayStrategy: toValidPriceDisplayType(props.requestedPriceDisplayStrategy ?? props.priceDisplayStrategy),
    developmentMode: props.developmentMode,
    displayVariant: props.displayVariant ?? (effectivePageId && upperFunnelPages.includes(effectivePageId) ? DisplayVariant.FROM : undefined),
    alignVAT: props.alignVAT || (effectivePageId === PageId.PDC ? AlignVAT.RIGHT : AlignVAT.BOTTOM),
    isTile: props.isTile ?? (effectivePageId ? upperFunnelPages.includes(effectivePageId) : false),
    alwaysShowMOQInfo: props.alwaysShowMOQInfo,
    locale: props.locale,
    tenant: props.tenant,
    hidePriceDetails: props.hidePriceDetails,
    docRefUrl: props.docRefUrl,
    assumeDocRequiresCertification: props.assumeDocRequiresCertification,
    showLogoChargeOnly: props.showLogoChargeOnly,
  };

  return <PriceDisplayV2 {...priceDisplayProps} />;
};

export default PriceDisplayTransitionWrapper;
